'use client';

import { RefObject, useEffect, useRef, useState } from 'react';

import { useMounted } from '../lifecycle/mount';

// 처음 한번만 실행되는 핸들러
export const observeIntersectionOnce = (handler: IntersectionObserverCallback): IntersectionObserverCallback => {
  return (entries, observer) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      handler(entries, observer);
      observer.disconnect();
    }
  };
};

export const Trigger = ({ onTrigger }: { onTrigger: () => void }) => {
  const ref = useRef<HTMLDivElement>(null);
  useIntersection(ref, (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting && entry.intersectionRatio > 0.5) onTrigger();
    }
  });
  return <div ref={ref} />;
};

export const useIntersection = (
  ref: RefObject<HTMLElement | null>,
  handler: IntersectionObserverCallback,
  options: IntersectionObserverInit = {},
) => {
  const handlerRef = useRef(handler);

  const isMounted = useMounted();

  const dom = ref.current;

  useEffect(() => {
    if (!isMounted) return;

    if (dom && typeof IntersectionObserver === 'function') {
      const observer = new IntersectionObserver(handlerRef.current, {
        root: options.root,
        rootMargin: options.rootMargin,
        threshold: options.threshold,
      });
      observer.observe(dom);

      return () => {
        observer.disconnect();
      };
    }
  }, [isMounted, dom, options.threshold, options.root, options.rootMargin]);
};

export const useIsElementInViewport = <E extends HTMLElement = HTMLDivElement | HTMLLIElement>(
  options?: IntersectionObserverInit,
) => {
  const optionsRef = useRef<IntersectionObserverInit>(options || { threshold: 0.4 });
  const ref = useRef<E | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useIntersection(
    ref,
    (entries) => {
      const [entry] = entries;
      setIsVisible(entry.intersectionRatio >= 0.4);
    },
    optionsRef.current,
  );
  return [ref, isVisible] as const;
};
